import { Display, setDisplay } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, ALIGN_SELF, FLEX_CONTAINER, FLEX_CONTAINER_COLUMN, FLEX_CONTAINER_ROW, FLEX_GAP, FLEX_ITEM, FLEX_ITEM_AUTO, H_100, MARGIN, PADDING } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { PropertyConfig, PropertyOptionsEditorWithPicker } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { Tooltip } from "../../bootstrap";
import { GeneratedFormOptions } from "../../components/generatedForm/generatedForm.types";
import { CjsIconFa } from "../../components/icon/iconFa";
import { getIconFaName } from "../../components/icon/iconFa.tools";
import { IconFA6 } from "../../components/icon/tools/fontAwesome6IconList.tools";
import { PopupValidationMode } from "../../components/showPopup/showPopup";
import { CjsEditor } from "../editor";
import { EDITOR_FACTORY_SERVICE, EditorFactoryService, NewEditorOptions } from "../editorFactory.service";
import { EditorWithPicker } from "../editorsWithPicker/editorWithPicker";
import { CjsReferenceListEditor } from "../referenceListEditor/referenceListEditor";

export type EditorMapConfig = Map<PropertyConfigType, (propertyConfig: PropertyConfig, options?: NewEditorOptions) => CjsEditor<unknown>>;

export interface FormItem {
    htmlElement: HTMLDivElement;
    editor: CjsEditor<unknown>;
    label: string;
    propertyConfig: PropertyConfig;
    /**
     * by default the propertyUid is the propertyConfig.propertyName 
     * but when a generated form mixes propertyConfigs for several objects, a propertyUid must be set 
     * because objectType 1 and objectType 2 may each have a "name" property for example
     * in this case, the propertyUid could be objectType1.name
     */
    propertyUid: string;
    section?: {
        tabUid?: string,
        accordionLabel?: string,
        displayOrder?: number
    };
}
export class FormFactory {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /**
     * Editor map configuration, used to build editors of FormItems
     * For a PropertyConfigType, return an Editor based on propertyConfig
     * Ex: Map({"STRING", (pc: PropertyConfig) => InputTextEditor}, ...etc)
     */
    private _editorMapConfig: EditorMapConfig = new Map();

    /** if _editorMapConfig does not contain the PropertyConfigType, we use _editorFactory as a fallback */
    @Injected(EDITOR_FACTORY_SERVICE)
    protected _editorFactory!: EditorFactoryService;

    /**
     * Get label of item
     */
    public getLabel(propertyConfig: PropertyConfig): string {
        return propertyConfig.label;
    }

    /**
     * Get editor of item
     */
    public getEditor(propertyConfig: PropertyConfig, options?: NewEditorOptions): CjsEditor<unknown> | undefined {
        const editorCallback = this._editorMapConfig.get(propertyConfig.typeUid);
        // try first from map , fallback on factory
        return editorCallback ? editorCallback(propertyConfig, options) : this._editorFactory.newEditor(propertyConfig, options);
    }

    /**
     * Generate a form item, based on a propertyConfig
     */
    public getFormItem(
        propertyConfig: PropertyConfig,
        p?: {
            editorOptions?: NewEditorOptions,
            generatedFormOptions?: GeneratedFormOptions<anyReallyAny>
        }
    ): FormItem | undefined {

        // Get from generatedForm options
        let isLabelInline = p?.generatedFormOptions?.layout?.formLabelLayout !== "block-level";

        // Override generatedForm option with property option if given
        const pcLabelLayout = propertyConfig.options?.layout?.labelLayout;
        if (pcLabelLayout) {
            isLabelInline = pcLabelLayout !== "block-level";
        }

        // Create form item div
        const formItemElement: HTMLDivElement = createHtmlElement("div", {
            cssClass: [FLEX_CONTAINER_ROW, "form-item", FLEX_ITEM_AUTO],
            attributes: {
                propertyName: propertyConfig.propertyName
            }
        });

        if (!isLabelInline) {
            formItemElement.classList.add(PADDING.PT_1);
        }

        const labelDivContent: AcceptedHtmlElementContent[] = [];

        const labelText = this.getLabel(propertyConfig);
        if (labelText !== "") {// If label is defined, build label div

            const labelTextContent: AcceptedHtmlElementContent[] = [labelText];

            if (propertyConfig.options?.tooltip && !propertyConfig.options?.doNotShowTooltipIcon) {
                labelTextContent.push(new CjsIconFa({ name: getIconFaName(IconFA6.CIRCLE_INFO) }));
            }

            const cssClasses = isLabelInline ? ["col-form-label", FLEX_ITEM] : ["form-label", MARGIN.MB_1];// Not centering labels anymore as we lose consistency on bigger editors (ex: RefListEditor)
            const labelTextDiv = createHtmlElement("div", {
                innerHTML: labelTextContent,
                cssClass: [...cssClasses, FLEX_CONTAINER, PADDING.PE_2, ALIGN_ITEMS.CENTER, FLEX_GAP.GAP_2]
            });

            const labelDivInlineClasses = [FLEX_ITEM, ALIGN_SELF.START];
            const labelDivBlockClasses = [ALIGN_SELF.START];

            let labelDivClasses = [FLEX_CONTAINER_ROW, "form-item-label"];
            labelDivClasses = labelDivClasses.concat(isLabelInline ? labelDivInlineClasses : labelDivBlockClasses);
            // Create label text div
            const labelDiv = createHtmlElement("div", {
                innerHTML: labelTextDiv,
                cssClass: labelDivClasses
            });

            // Add tooltip if needed
            if (propertyConfig.options?.tooltip) {
                // Handle string tooltip in a native title
                if (typeof propertyConfig.options.tooltip === "string") {
                    labelDiv.setAttribute("title", propertyConfig.options.tooltip);
                    labelDiv.setAttribute("data-toggle", "tooltip");
                }
                // Handle html string tooltip in a bootstrap tooltip
                else if (propertyConfig.options.tooltip.htmlString) {
                    new Tooltip(labelDiv, { title: propertyConfig.options.tooltip.htmlString, html: true });
                }
            }
            labelDivContent.push(labelDiv);
        }

        const formItemElementContent = createHtmlElement("div", {
            cssClass: [isLabelInline ? FLEX_CONTAINER_ROW : FLEX_CONTAINER_COLUMN, FLEX_ITEM_AUTO, ALIGN_ITEMS.CENTER, MARGIN.MX_2, MARGIN.MY_1],
            innerHTML: labelDivContent
        });

        // Add align-stretch to forItem for block label, needed to editors to take full width available
        if (!isLabelInline) { formItemElementContent.classList.add(ALIGN_ITEMS.STRETCH); }

        // Add label text div to form item
        formItemElement.append(formItemElementContent);

        // Create editor, can be undefined is propertyConfigType isn't set in editorConfigMap
        const editor = this.getEditor(propertyConfig, p?.editorOptions);
        if (editor) {
            editor.classList.add("form-item-editor", FLEX_ITEM_AUTO);

            // Set H_100 to handle referenceListEditor in form-item, to take all height
            if (editor instanceof CjsReferenceListEditor) {
                formItemElementContent.classList.add(H_100);
            }

            // Disable editor if required
            const formDisabled = getVOFValue(p?.generatedFormOptions?.disabled);
            if (formDisabled || getVOFValue(propertyConfig.options?.disabled)) {
                editor.options.setWithoutRefresh({ disabled: true });
            }

            // Set options for EditorWithPicker
            if (editor instanceof EditorWithPicker) {
                const editorWithPicker = editor as EditorWithPicker<anyReallyAny>;

                const editorOptions = propertyConfig.options as PropertyOptionsEditorWithPicker;
                // Add popup mode attribute if needed
                if (editorOptions?.picker?.popupMode) {
                    editorWithPicker.options.setWithoutRefresh({ popupMode: editorOptions.picker.popupMode as PopupValidationMode });
                }

                // Add picker only mode needed
                if (editorOptions?.picker?.only) {
                    editorWithPicker.options.setWithoutRefresh({ pickerOnly: editorOptions.picker.only });
                }

                // Add prefix
                if (propertyConfig.options?.prefix && editor instanceof EditorWithPicker) {
                    editorWithPicker.getInput().inputOptions.setWithoutRefresh({ inputPrefixText: propertyConfig.options.prefix });
                }

                // Add suffix
                if (propertyConfig.options?.suffix && editor instanceof EditorWithPicker) {
                    editorWithPicker.getInput().inputOptions.setWithoutRefresh({ inputSuffixText: propertyConfig.options.suffix });
                }
            }

            // Add editor to form item
            formItemElementContent.append(
                createHtmlElement("div", {
                    cssClass: [FLEX_CONTAINER, FLEX_ITEM_AUTO, "form-item-editor-container"],
                    innerHTML: editor
                })
            );

            // Don't display item if hidden is desired behavior
            if (getVOFValue(propertyConfig.options?.hidden)) {
                setDisplay(formItemElement, Display.NONE);
            }

            return {
                propertyUid: propertyConfig.propertyName,
                htmlElement: formItemElement,
                editor,
                propertyConfig,
                label: labelText,
                section: {
                    tabUid: propertyConfig.options?.section?.tabs?.uid,
                    accordionLabel: propertyConfig.options?.section?.group?.label,
                    displayOrder: propertyConfig.options?.section?.tabs?.displayOrder ?? propertyConfig.options?.section?.group?.displayOrder
                }
            };

        } else {
            this._log?.warn("FormItem cannot be build with propertyConfigType: " + propertyConfig.typeUid + ". Please specify: (propertyConfigType) => editor mapping in editorMapConfig.");
            return undefined;
        }
    }

    /** EditorMapConfig setter */
    public setEditorMapConfig(config: EditorMapConfig): void {
        this._editorMapConfig = config;
    }

    /** EditorMapConfig filled check */
    public isEditorMapConfigSet(): boolean {
        return this._editorMapConfig.size !== 0;
    }

}